import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { track } from './utils/track'
import { prefetchMapBinary } from './utils/mapCache'
import { MAP_ENABLED } from './config/features'
import { Sidebar } from './components/common/Sidebar'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { MusicPlayer, PlayHistoryPanel, RadioController } from './components/player'
import { DnDProvider } from './components/DragAndDrop/DnDProvider'
import { ReleasesPage } from './pages/ReleasesPage'
import { ArtistsPage } from './pages/ArtistsPage'
import { LabelsPage } from './pages/LabelsPage'
import { TrackingPage } from './pages/TrackingPage'
import { WantlistPage } from './pages/WantlistPage'
import { CollectionPage } from './pages/CollectionPage'
import { FolderPage } from './pages/FolderPage/FolderPage'
import { AuthCallbackPage } from './pages/AuthCallbackPage'
import { ReleasePage } from './pages/ReleasePage'
import { ShufflePage } from './pages/ShufflePage'
import { MapPage } from './pages/MapPage'
import { LoginPage } from './pages/LoginPage'
import { AdminPage } from './pages/AdminPage'
import { SettingsPage } from './pages/SettingsPage'
import { SellerPage } from './pages/SellerPage'
import { SellersPage } from './pages/SellersPage'
import { AudioEvalPage } from './pages/AudioEvalPage'
import { useAuth } from './context/AuthContext'
import { useGenres, useStyles, useIdlePrefetch } from './hooks'
import { IconButton } from './components/common'
import { Tour } from './components/Tour'
import { NotificationsBell } from './components/Notifications'
import { DiscogsWriteGateProvider } from './components/DiscogsWriteGate'
import { InviteFriendsModal } from './components/Invites/InviteFriendsModal'
import styles from './App.module.css'

// Normalize a pathname into a stable feature/page name for analytics.
function pageNameFromPath(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)
  if (seg.length === 0) return 'releases'
  const first = seg[0]
  if (first === 'releases' && seg.length > 1) return 'release-detail'
  if (first === 'folders') return 'folder'
  return first
}

// Logs one page_view per route change — covers every route (current and
// future) from one place, so individual pages don't need usePageView.
function RouteTracker() {
  const { pathname } = useLocation()
  useEffect(() => {
    track('page_view', { page: pageNameFromPath(pathname) })
  }, [pathname])
  return null
}

// Warm the genre/style lists after the initial page is ready. Their server-side
// count queries are synchronous, so keep them out of the critical load path.
function FacetPrefetch() {
  const [enabled, setEnabled] = useState(false)
  useIdlePrefetch(() => setEnabled(true))

  useGenres({ enabled })
  useStyles({ enabled })
  return null
}

// Warm the map cache only after the initial document load has finished. The
// payload is large, so it should never compete with the first page render.
function MapPrefetch() {
  useIdlePrefetch(prefetchMapBinary)
  return null
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return null
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function ResponsiveMapRoute() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches)

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)')
    const update = () => setIsMobile(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  if (!isMobile) return <MapPage />

  return (
    <div className={styles.mobileUnavailable}>
      <i className="bi bi-map"></i>
      <h1>The map is desktop only</h1>
      <p>The map needs a larger canvas. Open it on a tablet or desktop to explore your collection.</p>
    </div>
  )
}

function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showInvitePrompt, setShowInvitePrompt] = useState(false)
  const { user, isLoading } = useAuth()

  useEffect(() => {
    if (!user) return
    const promptKey = `tenwords:invite-prompt:v1:${user.id}`
    if (!localStorage.getItem(promptKey)) setShowInvitePrompt(true)
  }, [user])

  const closeInvitePrompt = () => {
    if (user) localStorage.setItem(`tenwords:invite-prompt:v1:${user.id}`, 'dismissed')
    setShowInvitePrompt(false)
  }

  if (isLoading) return null

  // Show login page without the main shell
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <DnDProvider>
     <DiscogsWriteGateProvider>
      <div className={styles.app}>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        {sidebarOpen && (
          <div className={styles.backdrop} onClick={() => setSidebarOpen(false)} />
        )}
        <IconButton
          variant="ghost"
          icon="bi bi-list"
          aria-label="Open menu"
          className={styles.hamburger}
          onClick={() => setSidebarOpen(true)}
        />
        <main className={styles.main}>
          <div className={styles.content}>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to="/releases" replace />} />
              <Route path="/login" element={<Navigate to="/releases" replace />} />
              <Route path="/releases" element={<ProtectedRoute><ReleasesPage /></ProtectedRoute>} />
              <Route path="/releases/:id" element={<ProtectedRoute><ReleasePage /></ProtectedRoute>} />
              <Route path="/artists" element={<ProtectedRoute><ArtistsPage /></ProtectedRoute>} />
              <Route path="/labels" element={<ProtectedRoute><LabelsPage /></ProtectedRoute>} />
              <Route path="/shuffle" element={<ProtectedRoute><ShufflePage /></ProtectedRoute>} />
              <Route path="/map" element={MAP_ENABLED ? <ProtectedRoute><ResponsiveMapRoute /></ProtectedRoute> : <Navigate to="/releases" replace />} />
              <Route path="/wantlist" element={<ProtectedRoute><WantlistPage /></ProtectedRoute>} />
              <Route path="/collection" element={<ProtectedRoute><CollectionPage /></ProtectedRoute>} />
              <Route path="/folders/:id" element={<ProtectedRoute><FolderPage /></ProtectedRoute>} />
              <Route path="/tracking" element={<ProtectedRoute><TrackingPage /></ProtectedRoute>} />
              <Route path="/sellers" element={<ProtectedRoute><SellersPage /></ProtectedRoute>} />
              <Route path="/seller" element={<ProtectedRoute><SellerPage /></ProtectedRoute>} />
              <Route path="/seller/:username" element={<ProtectedRoute><SellerPage /></ProtectedRoute>} />
              <Route path="/audio-eval" element={<ProtectedRoute><AudioEvalPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="*" element={<Navigate to="/releases" replace />} />
            </Routes>
            </ErrorBoundary>
          </div>
        </main>
        <RouteTracker />
        <FacetPrefetch />
        {MAP_ENABLED && <MapPrefetch />}
        <MusicPlayer />
        <PlayHistoryPanel />
        <RadioController />
        <NotificationsBell />
        <Tour />
        <InviteFriendsModal open={showInvitePrompt} onClose={closeInvitePrompt} />
      </div>
     </DiscogsWriteGateProvider>
    </DnDProvider>
  )
}

function App() {
  return <AppShell />
}

export default App
